import type { BotAccessUser } from './botAccessTypes';

export function normalizePersonName(name: string) {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function isGovernanceUserId(id: string) {
  return id.startsWith('gov-');
}

function mergeUserRecord(existing: BotAccessUser, incoming: BotAccessUser): BotAccessUser {
  const keepExisting =
    !isGovernanceUserId(existing.id) && isGovernanceUserId(incoming.id);
  const primary = keepExisting ? existing : incoming;
  const secondary = keepExisting ? incoming : existing;
  return {
    ...primary,
    isPrimary: primary.isPrimary || secondary.isPrimary,
    relationType: primary.relationType || secondary.relationType,
    email: primary.email || secondary.email,
    telegramId: primary.telegramId || secondary.telegramId,
    firstName: primary.firstName || secondary.firstName,
    lastName: primary.lastName || secondary.lastName,
  };
}

export function mergeAccessUsers(...lists: (BotAccessUser[] | undefined)[]): BotAccessUser[] {
  const map = new Map<string, BotAccessUser>();
  for (const list of lists) {
    if (!list) continue;
    for (const user of list) {
      const key = normalizePersonName(user.name);
      if (!key) continue;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, user);
        continue;
      }
      map.set(key, mergeUserRecord(existing, user));
    }
  }
  return [...map.values()];
}

export function governanceFallbackUsers(owner?: string, pilots?: string): BotAccessUser[] {
  const raw = [owner || '', pilots || ''].join(' · ');
  const matches =
    raw.match(
      /\b[A-ZÀ-ÖØ-Ý][a-zà-öø-ÿ]+(?:[-'][A-ZÀ-ÖØ-Ý][a-zà-öø-ÿ]+)?(?:\s+[A-ZÀ-ÖØ-Ý][a-zà-öø-ÿ]+)+\b/g
    ) || [];
  return [...new Set(matches)].map((name) => ({
    id: `gov-${name.toLowerCase().replace(/\s+/g, '-')}`,
    name,
  }));
}

export function usersFromTelegramEntries(entries: unknown): BotAccessUser[] {
  if (!Array.isArray(entries)) return [];
  const users: BotAccessUser[] = [];
  entries.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;
    const row = entry as { id?: string; title?: string; category?: string; tabId?: string };
    if (!String(row.tabId || '').includes('telegram')) return;
    const name = String(row.title || '').trim();
    if (!name || /^telegram\s*\d+$/i.test(name)) return;
    users.push({
      id: String(row.id || `telegram-entry-${index}`),
      name,
      relationType: row.category ?? null,
    });
  });
  return users;
}

export function sortAccessUsers(users: BotAccessUser[]): BotAccessUser[] {
  return [...users].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return a.name.localeCompare(b.name, 'fr');
  });
}
